import type {
  AgentQuestion,
  NetworkAccessRequest,
} from '@wandb/weave/lib/WbAgent/WbAgentClient';

import {assistantMessageId, userMessageId} from '../runtime/turn';
import {getActiveBranch} from './messageTree';
import {
  type ActiveThreadLoadFailure,
  type AgentChatState,
  findThread,
  type MessageNode,
  type RunOutcome,
  type Thread,
} from './thread';

export interface ActiveThreadLoadState {
  activeThreadId: string | null;
  failure: ActiveThreadLoadFailure | null;
  isLoading: boolean;
}

export function selectThreads(state: AgentChatState): Record<string, Thread> {
  return state.threads;
}

export function selectActiveThreadId(state: AgentChatState): string | null {
  return state.activeThreadId;
}

export function selectActiveThread(state: AgentChatState): Thread | undefined {
  return state.activeThreadId
    ? findThread(state, state.activeThreadId)
    : undefined;
}

/**
 * selectActiveThreadPendingQuestions returns the questions the active thread's
 * last completed turn asked, or null. The composer presents these as a guided
 * dialog for the next prompt.
 */
export function selectActiveThreadPendingQuestions(
  state: AgentChatState
): AgentQuestion[] | null {
  return selectActiveThread(state)?.pendingQuestions ?? null;
}

/**
 * selectActiveThreadPendingNetworkAccessRequest returns the elevated-network
 * request the active thread's last completed turn made, or null. The composer
 * presents this as a grant/deny element.
 */
export function selectActiveThreadPendingNetworkAccessRequest(
  state: AgentChatState
): NetworkAccessRequest | null {
  return selectActiveThread(state)?.pendingNetworkAccessRequest ?? null;
}

/**
 * selectActiveThreadActiveNetworkDomains returns the network allowlist the
 * latest turn ran with — the active grants the composer can show and revoke.
 */
export function selectActiveThreadActiveNetworkDomains(
  state: AgentChatState
): string[] {
  return selectActiveThread(state)?.activeNetworkDomains ?? [];
}

/**
 * selectIsOnNewChat returns true when the user is on a "new chat":
 * either there is no active thread, or the active thread is still a
 * client-side draft.
 */
export function selectIsOnNewChat(state: AgentChatState): boolean {
  const id = state.activeThreadId;
  return id === null || state.drafts[id] !== undefined;
}

export function selectActiveThreadLoadState(
  state: AgentChatState
): ActiveThreadLoadState {
  // `threadLoads` never holds an entry for a loaded thread (see the reducer's
  // loadThread/failLoadThread), so no loadStatus check is needed here.
  const activeThreadId = state.activeThreadId;
  const threadLoad =
    activeThreadId != null ? state.threadLoads[activeThreadId] : undefined;
  return {
    activeThreadId,
    isLoading: threadLoad?.status === 'loading',
    failure: threadLoad?.status === 'failed' ? threadLoad.failure : null,
  };
}

export function selectThreadBranch(
  state: AgentChatState,
  threadId: string,
  upToNodeId?: string
): MessageNode[] {
  const thread = findThread(state, threadId);
  return thread ? getActiveBranch(thread.messageTree, upToNodeId) : [];
}

export function selectActiveBranch(state: AgentChatState): MessageNode[] {
  return state.activeThreadId
    ? selectThreadBranch(state, state.activeThreadId)
    : [];
}

export function selectIsRunning(state: AgentChatState): boolean {
  const thread = selectActiveThread(state);
  return thread?.status === 'running';
}

/**
 * selectActiveThreadIsCancelling returns true while the active thread has an
 * in-flight run the user asked to stop but the server has not yet confirmed
 * terminal.
 */
export function selectActiveThreadIsCancelling(state: AgentChatState): boolean {
  const thread = selectActiveThread(state);
  return (
    thread != null && thread.status === 'running' && thread.cancelRequested
  );
}

/**
 * selectActiveThreadTurnOutcome returns the recorded terminal outcome for a
 * turn in the active thread, or undefined if the turn has not settled. Used to
 * render a per-turn marker such as a cancelled indicator.
 */
export function selectActiveThreadTurnOutcome(
  state: AgentChatState,
  turnId: string
): RunOutcome | undefined {
  return selectActiveThread(state)?.turnOutcomes[turnId];
}

export function selectThreadById(
  state: AgentChatState,
  threadId: string
): Thread | undefined {
  return findThread(state, threadId);
}

/**
 * selectHistoryItems projects a list of thread ids to their Thread entries,
 * dropping ids that are missing from the store or hidden by a pending or
 * confirmed delete.
 */
export function selectHistoryItems(
  state: AgentChatState,
  ids: readonly string[]
): Thread[] {
  const items: Thread[] = [];
  for (const id of ids) {
    if (state.pendingDeletes.has(id) || state.deletedThreadIds.has(id)) {
      continue;
    }

    const thread = state.threads[id];
    if (thread) {
      items.push(thread);
    }
  }
  return items;
}

/**
 * selectThreadDisplayTitle returns the renderable title for a thread,
 * preferring the pending-rename overlay when present, then the stored
 * title, then the prompt-snippet fallback, then a generic label.
 */
export function selectThreadDisplayTitle(
  state: AgentChatState,
  threadId: string
): string {
  const thread = findThread(state, threadId);
  const stored = state.pendingRenames[threadId]?.title ?? thread?.title;
  return stored?.trim() || thread?.promptSnippet || 'Untitled conversation';
}

/**
 * selectTurnPromptPageUrl returns the page URL captured in a turn's user
 * prompt (the `wandb-web-page-state` context part), or undefined when the turn
 * carried no page context (e.g. legacy threads). Looks up the turn's user node
 * in the given thread; the URL lets the turn's response offer a
 * "jump back to the originating page" affordance.
 */
export function selectTurnPromptPageUrl(
  state: AgentChatState,
  threadId: string,
  turnId: string
): string | undefined {
  const tree = findThread(state, threadId)?.messageTree;
  if (tree == null) {
    return undefined;
  }

  // A live turn's user node keeps its optimistic (client UUID) id because the
  // backend user message is skipped while polling, so it is not keyed as
  // `${turnId}:user`. Resolve it as the assistant node's parent first, then
  // fall back to the `:user` id used once the turn is reloaded from history.
  const parentOfAssistant = tree.parentId[assistantMessageId(turnId)];
  const node =
    (parentOfAssistant != null ? tree.nodes[parentOfAssistant] : undefined) ??
    tree.nodes[userMessageId(turnId)];
  if (node == null) {
    return undefined;
  }
  for (const part of node.content) {
    if (part.type === 'wandb-web-page-state' && part.url) {
      return part.url;
    }
  }
  return undefined;
}

/**
 * selectThreadIdByRootTurnId finds the thread ID for a given root turn ID.
 * Returns the thread ID if found, or null if no thread matches.
 */
export function selectThreadIdByRootTurnId(
  state: AgentChatState,
  rootTurnId: string
): string | null {
  return state.threads[rootTurnId] ? rootTurnId : null;
}
